import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageSkeleton } from "@/components/PageSkeleton";
import { PeriodFilter } from "@/components/PeriodFilter";
import { usePersistentPeriod } from "@/lib/usePersistentPeriod";
import { formatDate, formatTime } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users2, Clock, UserX, CheckCircle2, Trophy, AlertTriangle, Eye, MapPin, Stethoscope, Timer, FileText } from "lucide-react";

const EXCUSED_BADGE = {
  sakit: "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300",
  cuti: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300",
  izin: "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300",
  libur: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
};

const fmtMin = (m) => { m = Number(m || 0); if (m <= 0) return "0 mnt"; const h = Math.floor(m / 60), mm = m % 60; return h > 0 ? `${h}j ${mm}m` : `${mm} mnt`; };
const API = process.env.REACT_APP_BACKEND_URL;

const viewPhoto = async (path) => {
  if (!path) return;
  try {
    const r = await api.get(`/files/${path}`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  } catch { toast.error("Gagal memuat foto (mungkin sudah terhapus otomatis)"); }
};

export default function AttendanceDashboard() {
  const [period, setPeriod, range, setRange] = usePersistentPeriod("attendance-dash", "month");
  const [today, setToday] = useState(null);
  const [list, setList] = useState(null);
  const [rank, setRank] = useState(null);
  const [roleF, setRoleF] = useState("all");

  const qstr = () => period === "custom" ? `period=custom&start=${range.start}&end=${range.end}` : `period=${period}`;

  const exportPdf = async () => {
    try {
      const s = range.start || new Date().toISOString().slice(0, 10);
      const e = range.end || s;
      const r = await api.get(`/attendance?period=custom&start=${s}&end=${e}`);
      const rows = r.data.rows || [];
      const fmin = (m) => (Number(m || 0) > 0 ? `${m}` : "-");
      const byUser = {};
      rows.forEach((x) => {
        const g = byUser[x.user_name] = byUser[x.user_name] || { hari: 0, izin: 0, telat: 0, telatM: 0, lemburM: 0, awalM: 0 };
        if (x.leave) { g.izin += 1; return; }
        g.hari += 1;
        g.telatM += x.check_in?.late_minutes || 0;
        if (x.check_in?.status === "Terlambat") g.telat += 1;
        g.lemburM += x.check_out?.overtime_minutes || 0;
        g.awalM += x.check_out?.early_minutes || 0;
      });
      const detail = rows.map((x, i) => {
        const ci = x.check_in || {}, co = x.check_out || {}, loc = ci.location || {};
        return `<tr>
          <td>${i + 1}</td><td>${x.date}</td><td>${x.user_name}</td><td>${x.role}</td>
          <td>${ci.time ? formatTime(ci.time) : "-"}</td><td>${ci.status || "-"}</td><td>${fmin(ci.late_minutes)}</td>
          <td>${co.time ? formatTime(co.time) : "-"}</td><td>${co.status || "-"}</td><td>${fmin(co.overtime_minutes)}</td>
          <td>${x.leave ? (x.leave.type || "izin") : "-"}</td>
          <td>${loc.location_status || "-"}${loc.distance_m != null ? ` (${loc.distance_m} m)` : ""}</td>
          <td class="wrap">${[ci.note, co.note].filter(Boolean).join(" · ") || "-"}</td>
        </tr>`;
      }).join("");
      const rekap = Object.entries(byUser).map(([n, g]) => {
        const kek = g.telatM + g.awalM;
        return `<tr><td>${n}</td><td>${g.hari}</td><td>${g.izin}</td><td>${g.telat}</td><td>${g.telatM}</td><td>${g.lemburM}</td><td>${kek}</td><td>${Math.max(0, kek - g.lemburM)}</td></tr>`;
      }).join("");
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>Rekap Absensi</title><style>
        body{font-family:Arial,sans-serif;padding:24px;color:#111;font-size:11px}
        h1{font-size:16px;margin:0;color:#0B1F4B}.sub{color:#555;margin:2px 0 14px}
        .gold{color:#C9A227}table{width:100%;border-collapse:collapse;margin-bottom:18px}
        th{background:#0B1F4B;color:#fff;padding:6px 5px;text-align:left;font-size:10px}
        td{border:1px solid #ddd;padding:5px;font-size:10px}tr:nth-child(even) td{background:#f7f8fc}
        .wrap{max-width:160px}h2{font-size:13px;color:#0B1F4B;margin:14px 0 6px}
        .foot{margin-top:16px;font-size:9px;color:#888}
        @media print{body{padding:0}}</style></head><body>
        <h1>Rekap Absensi — <span class="gold">Lala Corporation Indonesia</span></h1>
        <p class="sub">Periode: ${formatDate(s)} s/d ${formatDate(e)} · Dicetak ${new Date().toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })} WIB</p>
        <h2>Rincian Harian</h2>
        <table><thead><tr><th>No</th><th>Tanggal</th><th>Nama</th><th>Peran</th><th>Masuk</th><th>Status Masuk</th><th>Telat (mnt)</th><th>Pulang</th><th>Status Pulang</th><th>Lembur (mnt)</th><th>Izin</th><th>Lokasi</th><th>Keterangan Kerja</th></tr></thead><tbody>${detail || '<tr><td colspan="13">Tidak ada data pada periode ini.</td></tr>'}</tbody></table>
        <h2>Rekap Per Pengguna</h2>
        <table><thead><tr><th>Nama</th><th>Hari Kerja</th><th>Izin</th><th>Terlambat (x)</th><th>Total Telat (mnt)</th><th>Lembur (mnt)</th><th>Kekurangan (mnt)</th><th>Sisa Kekurangan (mnt)</th></tr></thead><tbody>${rekap || '<tr><td colspan="8">-</td></tr>'}</tbody></table>
        <p class="foot">Dokumen ini dihasilkan otomatis oleh sistem pembukuan Lala Corporation Indonesia.</p>
        <scr` + `ipt>window.onload=function(){window.print();}</scr` + `ipt></body></html>`;
      const w = window.open("", "_blank");
      w.document.write(html);
      w.document.close();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };
  useEffect(() => { api.get("/attendance/today").then((r) => setToday(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, []);
  useEffect(() => {
    const rp = roleF !== "all" ? `&role=${roleF}` : "";
    api.get(`/attendance?${qstr()}${rp}`).then((r) => setList(r.data.rows));
    api.get(`/attendance/ranking?${qstr()}`).then((r) => setRank(r.data));
    /* eslint-disable-next-line */
  }, [period, range.start, range.end, roleF]);

  if (!today) return <PageSkeleton stats={4} rows={6} />;
  const st = today.stats;

  return (
    <div className="space-y-4" data-testid="attendance-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Dashboard Absensi</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Pantau kehadiran karyawan & admin. Terpisah dari dashboard pembukuan.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={exportPdf} data-testid="att-export-btn"><FileText className="h-3.5 w-3.5" /> Ekspor PDF</Button>
          <PeriodFilter value={period} onChange={setPeriod} range={range} onRangeChange={setRange} />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="att-dash-stats">
        {[
          { l: "Dijadwalkan", v: st.dijadwalkan, i: Users2, t: ["bg-sky-100 dark:bg-sky-950", "text-sky-600"] },
          { l: "Hadir Hari Ini", v: st.hadir, i: CheckCircle2, t: ["bg-emerald-100 dark:bg-emerald-950", "text-emerald-600"] },
          { l: "Terlambat", v: st.terlambat, i: Clock, t: ["bg-rose-100 dark:bg-rose-950", "text-rose-600"] },
          { l: "Belum Hadir", v: st.tidak_hadir, i: UserX, t: ["bg-amber-100 dark:bg-amber-950", "text-amber-600"] },
        ].map((c) => (
          <Card key={c.l} className="p-4" data-testid={`att-dash-stat-${c.l}`}>
            <div className="flex items-center gap-3">
              <div className={`grid place-items-center h-10 w-10 rounded-xl ${c.t[0]}`}><c.i className={`h-5 w-5 ${c.t[1]}`} /></div>
              <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{c.l}</p><p className="font-heading text-xl font-bold">{c.v}</p></div>
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-4">
        <h3 className="font-heading text-sm font-semibold mb-2 flex items-center gap-1.5"><UserX className="h-4 w-4 text-amber-500" /> Belum Hadir Hari Ini ({(today.belum_hadir || []).length})</h3>
        <div className="space-y-1.5" data-testid="att-absent-list">
          {(today.belum_hadir || []).length === 0 && <p className="text-sm text-emerald-600 py-3">Semua yang dijadwalkan sudah absen.</p>}
          {(today.belum_hadir || []).map((s) => (
            <div key={s.user_id} className="flex items-center gap-2 rounded-lg border border-amber-200/70 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-950/20 px-3 py-2 text-sm" data-testid={`att-absent-${s.user_id}`}>
              <UserX className="h-3.5 w-3.5 text-amber-500 shrink-0" />
              <span className="font-medium">{s.name}</span>
              <Badge variant="outline" className="text-[10px] uppercase">{s.role}</Badge>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="font-heading text-sm font-semibold mb-2 flex items-center gap-1.5"><Stethoscope className="h-4 w-4 text-violet-500" /> Izin / Sakit / Libur Hari Ini ({(today.izin_hari_ini || []).length})</h3>
        <div className="space-y-1.5" data-testid="att-excused-list">
          {(today.izin_hari_ini || []).length === 0 && <p className="text-sm text-muted-foreground py-3">Tidak ada yang izin, sakit, atau libur hari ini.</p>}
          {(today.izin_hari_ini || []).map((s) => (
            <div key={`${s.user_id}-${s.type}`} className="flex items-center gap-2 rounded-lg border border-violet-200/70 dark:border-violet-900/40 bg-violet-50/60 dark:bg-violet-950/20 px-3 py-2 text-sm" data-testid={`att-excused-${s.user_id}`}>
              <Stethoscope className="h-3.5 w-3.5 text-violet-500 shrink-0" />
              <span className="font-medium">{s.name}</span>
              <Badge variant="outline" className="text-[10px] uppercase">{s.role}</Badge>
              <Badge className={`text-[10px] capitalize ${EXCUSED_BADGE[s.type] || EXCUSED_BADGE.izin}`}>{s.type}</Badge>
              {s.note && <span className="text-[11px] text-muted-foreground truncate">· {s.note}</span>}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="font-heading text-sm font-semibold mb-2 flex items-center gap-1.5"><Timer className="h-4 w-4 text-indigo-500" /> Masih Bekerja — Belum Absen Pulang ({(today.belum_pulang || []).length})</h3>
        <p className="text-[11px] text-muted-foreground -mt-1 mb-2">Sudah absen masuk tapi belum absen pulang hari ini — berarti masih bekerja melewati hari.</p>
        <div className="space-y-1.5" data-testid="att-working-list">
          {(today.belum_pulang || []).length === 0 && <p className="text-sm text-muted-foreground py-3">Tidak ada — semua yang hadir sudah absen pulang.</p>}
          {(today.belum_pulang || []).map((s) => (
            <div key={s.user_id} className="flex items-center gap-2 rounded-lg border border-indigo-200/70 dark:border-indigo-900/40 bg-indigo-50/60 dark:bg-indigo-950/20 px-3 py-2 text-sm" data-testid={`att-working-${s.user_id}`}>
              <Timer className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
              <span className="font-medium">{s.name}</span>
              <Badge variant="outline" className="text-[10px] uppercase">{s.role}</Badge>
              <span className="text-[11px] text-muted-foreground">masuk {formatTime(s.check_in)}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="font-heading text-sm font-semibold mb-2">Absensi Hari Ini ({formatDate(today.date)})</h3>
        <div className="space-y-1.5" data-testid="att-today-list">
          {today.rows.length === 0 && <p className="text-sm text-muted-foreground py-4">Belum ada yang absen hari ini.</p>}
          {today.rows.map((r) => (
            <div key={r._id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`att-today-${r._id}`}>
              <div className="min-w-0">
                <p className="font-medium truncate">{r.user_name} <span className="text-[10px] text-muted-foreground uppercase">({r.role})</span></p>
                <p className="text-[11px] text-muted-foreground">Masuk {r.check_in ? formatTime(r.check_in.time) : "-"} · Pulang {r.check_out ? formatTime(r.check_out.time) : "-"}</p>
                {(r.check_in?.note || r.check_out?.note) && <p className="text-[11px] text-muted-foreground break-words">Kerja: {[r.check_in?.note, r.check_out?.note].filter(Boolean).join(" · ")}</p>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {r.leave && <Badge className={`text-[10px] capitalize ${EXCUSED_BADGE[r.leave.type] || EXCUSED_BADGE.izin}`}>Izin: {r.leave.type}</Badge>}
                {r.check_in?.location?.location_status && (
                  <Badge className={`text-[10px] ${String(r.check_in.location.location_status).startsWith("Di Luar") ? "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300" : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"}`} title={`Jarak: ${r.check_in.location.distance_m ?? "-"} m`} data-testid={`att-loc-${r._id}`}>
                    <MapPin className="h-2.5 w-2.5 mr-0.5 inline" />{r.check_in.location.location_status}
                  </Badge>
                )}
                {r.check_in?.status === "Terlambat" && <Badge className="text-[10px] bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300">Terlambat {fmtMin(r.check_in.late_minutes)}</Badge>}
                {r.check_in?.photo && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => viewPhoto(r.check_in.photo)} data-testid={`att-photo-in-${r._id}`} title="Foto masuk"><Eye className="h-3.5 w-3.5" /></Button>}
                {r.check_out?.photo && <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={() => viewPhoto(r.check_out.photo)} data-testid={`att-photo-out-${r._id}`} title="Foto pulang"><Eye className="h-3.5 w-3.5" /></Button>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4">
          <h3 className="font-heading text-sm font-semibold mb-2 flex items-center gap-1.5"><Trophy className="h-4 w-4 text-[#C9A227]" /> Top Teladan</h3>
          <div className="space-y-1.5" data-testid="att-top-teladan">
            {(rank?.top_teladan || []).length === 0 && <p className="text-xs text-muted-foreground py-3">Belum ada data.</p>}
            {(rank?.top_teladan || []).map((g, i) => (
              <div key={g.user_id} className="flex items-center justify-between gap-2 text-sm border-b border-border last:border-0 py-1.5">
                <span className="truncate"><b className="text-[#C9A227]">#{i + 1}</b> {g.user_name} <span className="text-[10px] text-muted-foreground">({g.role})</span></span>
                <span className="text-xs text-emerald-600 font-semibold shrink-0">Skor {g.skor} · hadir {g.hari}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-4">
          <h3 className="font-heading text-sm font-semibold mb-2 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-rose-500" /> Top Paling Terlambat</h3>
          <div className="space-y-1.5" data-testid="att-top-terlambat">
            {(rank?.top_terlambat || []).filter((g) => g.late_count > 0).length === 0 && <p className="text-xs text-muted-foreground py-3">Tidak ada keterlambatan pada periode ini.</p>}
            {(rank?.top_terlambat || []).filter((g) => g.late_count > 0).map((g, i) => (
              <div key={g.user_id} className="flex items-center justify-between gap-2 text-sm border-b border-border last:border-0 py-1.5">
                <span className="truncate"><b className="text-rose-500">#{i + 1}</b> {g.user_name} <span className="text-[10px] text-muted-foreground">({g.role})</span></span>
                <span className="text-xs text-rose-600 font-semibold shrink-0">{g.late_count}× · {fmtMin(g.late_minutes)} · rata {g.avg_late}m</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-2 gap-2">
          <h3 className="font-heading text-sm font-semibold">Rekap Absensi Periode</h3>
          <Select value={roleF} onValueChange={setRoleF}>
            <SelectTrigger className="w-36" data-testid="att-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Peran</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="karyawan">Karyawan</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5" data-testid="att-period-list">
          {list === null && <p className="text-xs text-muted-foreground py-3">Memuat...</p>}
          {list && list.length === 0 && <p className="text-sm text-muted-foreground py-4">Tidak ada data absensi pada periode ini.</p>}
          {(list || []).map((r) => (
            <div key={r._id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid={`att-period-${r._id}`}>
              <div className="min-w-0">
                <p className="font-medium truncate">{r.user_name} <span className="text-[10px] text-muted-foreground">({r.role}) · {formatDate(r.date)}</span></p>
                <p className="text-[11px] text-muted-foreground">Masuk {r.check_in ? formatTime(r.check_in.time) : "-"} · Pulang {r.check_out ? formatTime(r.check_out.time) : "-"}</p>
                {(r.check_in?.note || r.check_out?.note) && <p className="text-[11px] text-muted-foreground break-words">Kerja: {[r.check_in?.note, r.check_out?.note].filter(Boolean).join(" · ")}</p>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {r.check_in?.status === "Terlambat" && <Badge className="text-[10px] bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300">Telat {fmtMin(r.check_in.late_minutes)}</Badge>}
                {r.check_out?.overtime_minutes > 0 && <Badge className="text-[10px] bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300">Lembur {fmtMin(r.check_out.overtime_minutes)}</Badge>}
                {r.check_in?.photo && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => viewPhoto(r.check_in.photo)} title="Foto masuk"><Eye className="h-3.5 w-3.5" /></Button>}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
